// src/pages/Portfolio.js
import React, { useState, useEffect, useMemo } from "react";
import "../styles/Portfolio.css";
import Footer from "../components/Footer";

// Firestore
import { db } from "../firebase";
import { doc, getDoc, collection, getDocs, query, orderBy } from "firebase/firestore";

const DEFAULT_CATEGORY_ORDER = [
  "AI Tools Proficiency",
  "Discord Server",
  "UI/UX Design",
  "Presentation Design",
  "Content Writing",
  "Video Editing",
  "Social Media Creatives",
];

const Portfolio = () => {
const [portfolioData, setPortfolioData] = useState([]);
const [pageSettings, setPageSettings] = useState({ 
  heroImage: "", 
  heroTitle: "Portfolio", 
  heroSubtitle: "A showcase of my recent designs.", 
  categoryOrder: DEFAULT_CATEGORY_ORDER, 
  ctaText: "See What Clients Say", 
  ctaLink: "/testimonials", 
  }); 

  const [selectedCategory, setSelectedCategory] = useState(DEFAULT_CATEGORY_ORDER[0]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [loading, setLoading] = useState(true);
  const [loadingHero, setLoadingHero] = useState(true);
  const [error, setError] = useState("");

  // Fetch items
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const itemsCol = collection(db, "pageContent", "portfolio", "items");
        const q = query(itemsCol, orderBy("order", "asc"));
        const snap = await getDocs(q);
        if (!mounted) return;

        const items = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((it) => it && it.title);

        setPortfolioData(items);
      } catch (err) {
        console.error("Error fetching portfolio items:", err);
        if (mounted) {
          setPortfolioData([]);
          setError("Failed to load portfolio projects. Please try again.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, []);

  // Fetch hero + optional categoryOrder
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const ref = doc(db, "pageContent", "portfolio");
        const snap = await getDoc(ref);
        if (!mounted) return;
        if (snap.exists()) {
          const data = snap.data();

          const catOrder =
            Array.isArray(data.categoryOrder) && data.categoryOrder.length
              ? data.categoryOrder
              : DEFAULT_CATEGORY_ORDER;

          setPageSettings((prev) => ({ 
            ...prev, 
            heroImage: data.heroBackground || data.heroImage || prev.heroImage, 
            heroTitle: data.heroTitle || data.heading || prev.heroTitle, 
            heroSubtitle: data.heroSubtitle || data.introText || prev.heroSubtitle, 
            categoryOrder: catOrder, 
            ctaText: data.ctaText || prev.ctaText, 
            ctaLink: data.ctaLink || prev.ctaLink, 
          })); 

          if (!catOrder.includes(selectedCategory)) {
            setSelectedCategory(catOrder[0]);
            setCurrentIndex(0);
          }
        }
      } catch (err) {
        console.error("Error fetching portfolio page settings:", err);
      } finally {
        if (mounted) setLoadingHero(false);
      }
    })();
    return () => (mounted = false);
  }, []); // mount only

  // Keep category & index in sync
  useEffect(() => {
    const list = pageSettings.categoryOrder || DEFAULT_CATEGORY_ORDER;
    setSelectedCategory(list[currentIndex]);
  }, [currentIndex, pageSettings.categoryOrder]);

  const filteredProjects = useMemo(
    () => portfolioData.filter((p) => p.category === selectedCategory),
    [portfolioData, selectedCategory]
  );

  return (
    <div className="portfolio-container">
      {/* Hero Section */}
      <div
        className="portfolio-hero"
        style={{
          backgroundImage: pageSettings.heroImage ? `url(${pageSettings.heroImage})` : undefined,
        }}
      >
        <div className="portfolio-overlay" />
        <div className="portfolio-text">
          <h1>{pageSettings.heroTitle}</h1>
          <p dangerouslySetInnerHTML={{ __html: pageSettings.heroSubtitle }} />
        </div>
            </div>

              {/* Category Filters (Desktop) */}
      <div className="portfolio-filter-shell">
        <div className="portfolio-filters">
          {(pageSettings.categoryOrder || DEFAULT_CATEGORY_ORDER).map((category, index) => (
            <button
              key={category}
              className={`portfolio-filter ${
                selectedCategory === category ? "active" : ""
              }`}
              onClick={() => {
                setSelectedCategory(category);
                setCurrentIndex(index);
              }}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Carousel (Mobile) */}
      <div className="portfolio-carousel">
        <button
          className="portfolio-carousel-arrow"
          onClick={() => setCurrentIndex((i) => Math.max(i - 1, 0))}
          disabled={currentIndex === 0}
          aria-label="Previous category"
        >
          ←
        </button>

        <span className="carousel-category">
          {(pageSettings.categoryOrder || DEFAULT_CATEGORY_ORDER)[currentIndex]}
        </span>

        <button
          className="portfolio-carousel-arrow"
          onClick={() =>
            setCurrentIndex((i) =>
              Math.min(
                i + 1,
                (pageSettings.categoryOrder || DEFAULT_CATEGORY_ORDER).length - 1
              )
            )
          }
          disabled={
            currentIndex === (pageSettings.categoryOrder || DEFAULT_CATEGORY_ORDER).length - 1
          }
          aria-label="Next category"
        >
          →
        </button>
      </div>
      <div className="page-body-with-doodles"></div>

      <div className="page-body-with-doodles">
      {/* Project Cards */}
      <div className="portfolio-cards">
        {loading || loadingHero ? (
          <div className="portfolio-skeleton-wrap">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="portfolio-skeleton-card" key={i} />
            ))}
          </div>
        ) : error ? (
          <p className="portfolio-empty">{error}</p>
        ) : filteredProjects.length > 0 ? (
          filteredProjects.map((project, index) => (
            <a
              className="portfolio-card"
              key={project.id || index}
              href={project.link || "#"}
              target={project.link ? "_blank" : "_self"}
              rel="noopener noreferrer"
            >
              <div className="portfolio-image-wrapper">
                {/* inner photo window that clips the image */}
                <div className="portfolio-photo-window">
                  <img
                    src={project.image}
                    alt={project.title || "Project"}
                    className="portfolio-photo-inside"
                    loading="lazy"
                    onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                  />
                </div>

                <div className="portfolio-content-inside">
                  <h3>{project.title || "Untitled Project"}</h3>
                  {project.description ? <p>{project.description}</p> : null}
                </div>
              </div>
            </a>
          ))
        ) : (
          <p className="portfolio-empty">No projects added under this category yet!</p>
        )}
            </div> 

      <div className="portfolio-cta">
        <a href={pageSettings.ctaLink} className="portfolio-cta-button">
          {pageSettings.ctaText}
        </a>
      </div>
            </div>

      <Footer /> 
    </div>
  );
};

export default Portfolio;
